import { LucideIcon, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4 rounded-lg p-8 text-center",
        className
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10">
        <Icon className="h-6 w-6 text-primary" strokeWidth={1.75} aria-hidden="true" />
      </div>

      <div className="space-y-1 max-w-sm">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        {description && (
          <p className="text-sm text-foreground-secondary leading-relaxed">
            {description}
          </p>
        )}
      </div>

      {action && (
        <button
          onClick={action.onClick}
          className={cn(
            "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold mt-1",
            "bg-primary text-primary-foreground hover:bg-primary-dark",
            "focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 focus:ring-offset-background",
            "transition-colors duration-base"
          )}
          type="button"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

export function CompactEmptyState({
  message,
  icon: Icon = Inbox,
  className,
}: {
  message: string;
  icon?: LucideIcon;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-center gap-2.5 py-8 text-sm text-foreground-secondary",
        className
      )}
      role="status"
    >
      <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 shrink-0">
        <Icon className="h-3.5 w-3.5 text-primary" strokeWidth={1.75} aria-hidden="true" />
      </div>
      <span>{message}</span>
    </div>
  );
}
